import getEnumValue from "../utils/getEnumValue";
import { APP_NAME_TO_DEF, APPLICATIONS_ID, APPLICATIONS_NAME } from "./domains";
import { ENV_DEVELOPMENT_ID, ENV_LOCAL_ID, ENV_PRODUCTION_ID } from "./environments";
import { API_URL, API_URL_ASSETS, API_URL_BASE, APP_NAME, APPLICATION_VERSION, ASSETS_URL, ATTRIBUTION_SIGNING_SECRET, BUILD_MODE, DOMAIN, ENVIRONMENT, FIREBASE_API_KEY, FIREBASE_APP_ID, FIREBASE_AUTH_DOMAIN, FIREBASE_MESSAGING_SENDER_ID, FIREBASE_PROJECT_ID, FIREBASE_STORAGE_BUCKET, GROWTHBOOK_CLIENT_KEY, HOST, JWT_PUBLIC_KEY, KINDE_CLIENT_ID, KINDE_CONNECTION_APPLE, KINDE_CONNECTION_DISCORD, KINDE_CONNECTION_EMAIL_PASSWORDLESS, KINDE_CONNECTION_GOOGLE, KINDE_CONNECTION_X, KINDE_DOMAIN, KINDE_ORG, KINDE_USE_LOCAL_STORAGE, LOAD_AS_APPLICATION_ID, LOGROCKET_STAGING, ORG_CODE, QUEUE_RETRY_TIME_SECONDS, RECOMBEE_DB_NAME, RECOMBEE_PUBLIC_TOKEN, RECOMBEE_REGION, RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE, RESET_3RD_PARTIES, REVENUECAT_ANDROID_API_KEY, REVENUECAT_IOS_API_KEY, SINGULAR_API_KEY, SINGULAR_API_SECRET, SINGULAR_FB_APP_ID, SOCKET_SERVER, SPICYCHAT_URL, SUPPORT, TRACKJS_APPLICATION, TRACKJS_TOKEN, TYPESENSE_FALLBACK_HOST_1, TYPESENSE_FALLBACK_HOST_2, TYPESENSE_FALLBACK_HOST_3, TYPESENSE_FALLBACK_HOST_4, TYPESENSE_FALLBACK_HOST_5, TYPESENSE_HOST, TYPESENSE_HOST1, TYPESENSE_HOST2, TYPESENSE_HOST3, TYPESENSE_HOST4, TYPESENSE_HOST5, WITH_CREDENTIALS, } from "./envVariables";
import { NATIVE_PLATFORM_OS } from "./envVariables/envVariablesNative";
import { NATIVE_PLATFORM, PLATFORM, WEB_PLATFORM } from "./platform";
import resolveWebPlatformOs from "./resolveWebPlatformOs";
function resolveHostname(platform) {
    if (platform !== WEB_PLATFORM || typeof window === "undefined") {
        return undefined;
    }
    return window.location.hostname.toLowerCase();
}
function hostnameMatches({ hostname, configuredHostname }) {
    return hostname === configuredHostname || hostname.endsWith(`.${configuredHostname}`);
}
function hostnameMatchesExactly({ hostname, definition }) {
    return hostname === definition.domain || definition.testDomains.some((testDomain) => hostname === testDomain);
}
function hostnameMatchesParentDomain({ hostname, definition }) {
    const hasMatchingTestDomain = definition.testDomains.some((testDomain) => hostnameMatches({ hostname, configuredHostname: testDomain }));
    return hostnameMatches({ hostname, configuredHostname: definition.domain }) || hasMatchingTestDomain;
}
function findApplicationDefinitionForHostname(hostname) {
    if (!hostname) {
        return undefined;
    }
    for (const definition of APP_NAME_TO_DEF.values()) {
        if (hostnameMatchesExactly({ hostname, definition })) {
            return definition;
        }
    }
    for (const definition of APP_NAME_TO_DEF.values()) {
        if (hostnameMatchesParentDomain({ hostname, definition })) {
            return definition;
        }
    }
    return undefined;
}
function normalizeApplicationId(applicationId) {
    return applicationId?.trim().toLowerCase() || APPLICATIONS_ID.SpicyChat;
}
function findApplicationDefinitionForLoadAsId(loadAsId) {
    const applicationName = getEnumValue(APPLICATIONS_NAME, loadAsId, true);
    if (!applicationName) {
        return undefined;
    }
    return APP_NAME_TO_DEF.get(applicationName);
}
function resolveApplicationDefinition({ platform, hostname, }) {
    if (platform !== WEB_PLATFORM) {
        return undefined;
    }
    return findApplicationDefinitionForHostname(hostname) ?? findApplicationDefinitionForLoadAsId(LOAD_AS_APPLICATION_ID);
}
function resolveIsPartnerApp({ platform, hostname }) {
    if (platform !== WEB_PLATFORM) {
        return false;
    }
    return Boolean(resolveApplicationDefinition({ platform, hostname })?.isPartnerApp());
}
function resolveApplicationId({ platform, hostname }) {
    if (platform === WEB_PLATFORM) {
        return normalizeApplicationId(resolveApplicationDefinition({ platform, hostname })?.id ?? LOAD_AS_APPLICATION_ID);
    }
    return normalizeApplicationId(APP_NAME);
}
function resolveAppName(applicationId) {
    for (const definition of APP_NAME_TO_DEF.values()) {
        if (definition.id === applicationId) {
            return definition.name;
        }
    }
    return applicationId;
}
function resolveMode({ platform, hostname }) {
    if (platform === WEB_PLATFORM && (hostname === "localhost" || hostname === "127.0.0.1")) {
        return ENV_LOCAL_ID;
    }
    return BUILD_MODE?.toLowerCase().includes(ENV_PRODUCTION_ID) ? ENV_PRODUCTION_ID : ENV_DEVELOPMENT_ID;
}
function resolveOs(platform) {
    if (platform === WEB_PLATFORM) {
        return resolveWebPlatformOs();
    }
    return NATIVE_PLATFORM_OS === "IOS" || NATIVE_PLATFORM_OS === "ANDROID" ? NATIVE_PLATFORM_OS : undefined;
}
const HOSTNAME = resolveHostname(PLATFORM);
const IS_PARTNER_APP = resolveIsPartnerApp({ platform: PLATFORM, hostname: HOSTNAME });
const APPLICATION_ID = resolveApplicationId({ platform: PLATFORM, hostname: HOSTNAME });
const basicAppConfig = Object.freeze({
    platform: PLATFORM,
    os: resolveOs(PLATFORM),
    applicationId: APPLICATION_ID,
    isPartnerApp: IS_PARTNER_APP,
    appName: resolveAppName(APPLICATION_ID),
    appVersion: APPLICATION_VERSION ?? "0.0.0",
    mode: resolveMode({ platform: PLATFORM, hostname: HOSTNAME }),
    apiUrl: API_URL ?? "",
    kindeClientId: KINDE_CLIENT_ID ?? "",
    apiUrlAssets: API_URL_ASSETS,
    apiUrlBase: API_URL_BASE,
    assetsUrl: ASSETS_URL,
    attributionSigningSecret: ATTRIBUTION_SIGNING_SECRET,
    domain: DOMAIN,
    environment: ENVIRONMENT,
    firebaseApiKey: FIREBASE_API_KEY,
    firebaseAppId: FIREBASE_APP_ID,
    firebaseAuthDomain: FIREBASE_AUTH_DOMAIN,
    firebaseMessagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
    firebaseProjectId: FIREBASE_PROJECT_ID,
    firebaseStorageBucket: FIREBASE_STORAGE_BUCKET,
    growthbookClientKey: GROWTHBOOK_CLIENT_KEY,
    host: HOST,
    jwtPublicKey: JWT_PUBLIC_KEY,
    kindeConnectionApple: KINDE_CONNECTION_APPLE,
    kindeConnectionDiscord: KINDE_CONNECTION_DISCORD,
    kindeConnectionEmailPasswordless: KINDE_CONNECTION_EMAIL_PASSWORDLESS,
    kindeConnectionGoogle: KINDE_CONNECTION_GOOGLE,
    kindeConnectionX: KINDE_CONNECTION_X,
    kindeDomain: KINDE_DOMAIN,
    kindeOrg: KINDE_ORG,
    kindeUseLocalStorage: KINDE_USE_LOCAL_STORAGE,
    loadAsApplicationId: LOAD_AS_APPLICATION_ID,
    logrocketStaging: LOGROCKET_STAGING,
    orgCode: ORG_CODE,
    queueRetryTimeSeconds: QUEUE_RETRY_TIME_SECONDS,
    recombeeDbName: RECOMBEE_DB_NAME,
    recombeePublicToken: RECOMBEE_PUBLIC_TOKEN,
    recombeeRegion: RECOMBEE_REGION,
    recombeeRequestTimeoutLocalStorage: RECOMBEE_REQUEST_TIMEOUT_LOCAL_STORAGE,
    reset3rdParties: RESET_3RD_PARTIES,
    revenuecatAndroidApiKey: REVENUECAT_ANDROID_API_KEY,
    revenuecatIosApiKey: REVENUECAT_IOS_API_KEY,
    singularApiKey: SINGULAR_API_KEY,
    singularApiSecret: SINGULAR_API_SECRET,
    singularFbAppId: SINGULAR_FB_APP_ID,
    socketServer: SOCKET_SERVER,
    spicychatUrl: SPICYCHAT_URL,
    support: SUPPORT,
    trackjsApplication: TRACKJS_APPLICATION,
    trackjsToken: TRACKJS_TOKEN,
    typesenseHost: TYPESENSE_HOST,
    typesenseHost1: TYPESENSE_HOST1,
    typesenseHost2: TYPESENSE_HOST2,
    typesenseHost3: TYPESENSE_HOST3,
    typesenseHost4: TYPESENSE_HOST4,
    typesenseHost5: TYPESENSE_HOST5,
    typesenseFallbackHost1: TYPESENSE_FALLBACK_HOST_1,
    typesenseFallbackHost2: TYPESENSE_FALLBACK_HOST_2,
    typesenseFallbackHost3: TYPESENSE_FALLBACK_HOST_3,
    typesenseFallbackHost4: TYPESENSE_FALLBACK_HOST_4,
    typesenseFallbackHost5: TYPESENSE_FALLBACK_HOST_5,
    withCredentials: WITH_CREDENTIALS,
});
export default basicAppConfig;
